import {Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {MenuItem, MessageService} from 'primeng/api';
import {NavigationService} from '../../../../service/navigation.service';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {AccountService} from '../../../../service/account.service';
import {AccountInterface} from '../../../../model/account.interface';
import {IpopConfigurationService} from '../../../../service/ipop-configuration.service';
import {IpopConfiguration} from '../../../../model/ipop-configuration.model';
import {OfferRegistryProcessorSyncService} from '../../../../service/offer-registry-processor-sync.service';
import {OfferRegistryProcessStatusInterface} from '../../../../model/offer-registry-process-status.interface';
import {finalize, repeat, Subject, takeUntil} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';

const processStatusPollingIntervalMs = 3000;
const duplicateQueueStatusCode = 409;
const rulesProcessingToastDetail = 'The rules are being processed, offer data will be processed and synced within the next 10 minutes.';
const processStatusLoadErrorDetail = 'Unable to retrieve offer registry process status';
const processQueueErrorDetail = 'Unable to queue rule processing';
const dirtyRuleNavigationConfirmationMessage = 'Offer Set Rules are being processed for this account. Changes may not be visible immediately while processing is in progress.';

@Component({
    selector: 'auto-ipop-configuration',
    templateUrl: './ipop-configuration.component.html',
    styleUrls: ['./ipop-configuration.component.scss'],
    standalone: false
})
export class IpopConfigurationComponent implements OnInit, OnDestroy {
  accountId: number;
  settingsSideNav: MenuItem[];
  account: AccountInterface;
  privateOffersForm: UntypedFormGroup;
  vdpOffersForm: UntypedFormGroup;
  ipopConfiguration: IpopConfiguration | null = null;
  isSaving = false;
  isProcessingRules = false;
  isProcessStatusInitialized = false;
  isOfferRegistryProcessingActive = false;
  isRuleConfigurationDirty = false;
  activeTab = 'privateOffers';
  processStatus: OfferRegistryProcessStatusInterface | null = null;
  private isProcessStatusLoadFailed = false;
  private isComponentDestroyed = false;
  private processStatusPollingSubject: Subject<void> = new Subject<void>();
  private destroySubject: Subject<void> = new Subject<void>();

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    private navigationService: NavigationService,
    protected messageService: MessageService,
    protected accountService: AccountService,
    private ipopConfigurationService: IpopConfigurationService,
    private offerRegistryProcessorSyncService: OfferRegistryProcessorSyncService
  ) {
  }

  async ngOnInit(): Promise<void> {
    this.titleService.setTitle('IPOP');
    this.route.paramMap.subscribe((params) => {
      const idParam = params.get('accountId');
      if (idParam) {
        this.accountId = parseInt(idParam, 10);
        if (this.isValidAccountId()) {
          this.initNavigation();
          this.loadSettings();
          this.loadProcessStatus();
        } else {
          this.handleInvalidAccountId();
        }
      }
    });

    this.route.queryParamMap.subscribe((queryParams) => {
      const requestedTab = queryParams.get('tab');

      if (requestedTab === 'offerSetRules') {
        this.activeTab = 'offerSetRules';

        return;
      }

      if (requestedTab === 'vdpOffers') {
        this.activeTab = 'vdpOffers';

        return;
      }

      this.activeTab = 'privateOffers';
    });
  }

  /**
   * Cleanup component subscriptions.
   */
  ngOnDestroy(): void {
    this.isComponentDestroyed = true;
    this.cancelProcessStatusPolling();
    this.processStatusPollingSubject.complete();
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  /**
   * Initialize the side navigation menu
   */
  initNavigation(): void {
    this.navigationService
      .getMenuItems(this.accountId, 'settings-view-side-menu.configuration.json')
      .then((navItems) => (this.settingsSideNav = navItems));
  }

  /**
   * Load account and IPOP configurations
   */
  private loadSettings(): void {
    this.accountService.getAccount(this.accountId).subscribe({
      next: (account) => {
        this.account = account;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load account'
        });
      }
    });

    this.ipopConfigurationService.getSettings(this.accountId).subscribe({
      next: (settings) => {
        this.ipopConfiguration = settings;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load settings'
        });
      }
    });
  }

  /**
   * Initialize the private offers form group
   */
  onPrivateOffersFormGroupInitialized(form: UntypedFormGroup) {
    this.privateOffersForm = form;
  }

  /**
   * Initialize the VDP offers form group
   */
  onVdpOffersFormGroupInitialized(form: UntypedFormGroup) {
    this.vdpOffersForm = form;
  }

  /**
   * Check if the save button should be disabled
   */
  getSaveButtonDisabled(): boolean {
    let isDisabled = true;

    if (this.privateOffersForm && this.privateOffersForm.dirty && this.privateOffersForm.valid) {
      isDisabled = false;
    }
    if (this.vdpOffersForm && this.vdpOffersForm.dirty && this.vdpOffersForm.valid) {
      isDisabled = false;
    }
    if (this.isOfferRegistryProcessingActive || this.isProcessingRules || this.isProcessStatusLoadFailed) {
      isDisabled = true;
    }

    return isDisabled;
  }

  /**
   * Checks whether the Process Rules button should be disabled.
   */
  getProcessRulesButtonDisabled(): boolean {
    return !this.isProcessStatusInitialized
      || this.isOfferRegistryProcessingActive
      || this.isProcessingRules
      || this.isProcessStatusLoadFailed
      || !this.isValidAccountId();
  }

  /**
   * Queues account-level processing for all account rules.
   */
  processRules(): void {
    if (!this.getProcessRulesButtonDisabled()) {
      this.queueProcessRulesForAllAccountRules(true);
    }
  }

  /**
   * Checks whether rule configuration has unprocessed changes.
   */
  hasDirtyRuleConfiguration(): boolean {
    return this.isRuleConfigurationDirty === true;
  }

  /**
   * Queues account-level processing for dirty rules during navigation attempts.
   */
  queueDirtyRuleProcessingForNavigationAttempt(): void {
    if (this.shouldQueueDirtyRuleProcessingForNavigation()) {
      this.queueProcessRulesForAllAccountRules(false);
    }
  }

  /**
   * Marks rule configuration changes as dirty for later child-component wiring.
   */
  handleRuleConfigurationChanged(): void {
    this.isRuleConfigurationDirty = true;
  }

  /**
   * Save IPOP configuration
   */
  saveSettings(): void {
    let payload: IpopConfiguration | null = null;

    if (this.ipopConfiguration) {
      this.isSaving = true;
      payload = this.buildSaveSettingsPayload(this.ipopConfiguration);
    }

    if (payload) {
      this.ipopConfigurationService.updateSettings(payload).subscribe({
        next: () => {
          this.handleSaveSuccess();
        },
        error: () => {
          this.handleSaveError();
        },
        complete: () => {
          this.handleSaveComplete();
        }
      });
    }
  }

  /**
   * Build the full IPOP configuration payload from the current forms.
   */
  private buildSaveSettingsPayload(ipopConfiguration: IpopConfiguration): IpopConfiguration {
    const includePrivateOffersInExternalAdvertising = this.resolveBooleanControlValue(
      this.privateOffersForm,
      'includePrivateOffersInExternalAdvertising',
      false
    );
    const unlockPrivateOffersEnabled = this.resolveBooleanControlValue(this.privateOffersForm, 'unlockPrivateOffersEnabled', false);
    const displaySavingsMessage = this.resolveBooleanControlValue(this.privateOffersForm, 'displaySavingsMessage', true);
    const savingsMessage = this.resolveStringControlValue(this.privateOffersForm, 'savingsMessage', 'Instant Savings Unlocked');
    const savingsMessageBackgroundColor = this.resolveStringControlValue(this.privateOffersForm, 'savingsMessageBackgroundColor', '#DDE9E2');
    const showPriceComparison = this.resolveBooleanControlValue(this.privateOffersForm, 'showPriceComparison', false);
    const displayUnlockedSellingPrice = this.resolveBooleanControlValue(this.privateOffersForm, 'displayUnlockedSellingPrice', false);
    const preUnlockCtaText = this.resolveStringControlValue(this.privateOffersForm, 'preUnlockCtaText', 'Unlock Your Special Offer');
    const postUnlockCtaText = this.resolveStringControlValue(this.privateOffersForm, 'postUnlockCtaText', 'Claim Your Special Offer');
    const privateOfferTextColor = this.resolveStringControlValue(this.privateOffersForm, 'privateOfferTextColor', '#000000');
    const privateOfferButtonColor = this.resolveStringControlValue(this.privateOffersForm, 'privateOfferButtonColor', '#16A34A');

    const vdpOfferWidgetEnabled = this.resolveBooleanControlValue(this.vdpOffersForm, 'vdpOfferWidgetEnabled', false);
    const offerTextColor = this.resolveStringControlValue(this.vdpOffersForm, 'offerTextColor', '#000000');
    const ctaTextFontSize = this.resolveNumericControlValue(this.vdpOffersForm, 'ctaTextFontSize', 22);
    const ctaButtonColor = this.resolveStringControlValue(this.vdpOffersForm, 'ctaButtonColor', '#000000');
    const clickToCallEnabled = this.resolveBooleanControlValue(this.vdpOffersForm, 'clickToCallEnabled', true);
    const clickToCallText = this.resolveStringControlValue(this.vdpOffersForm, 'clickToCallText', '[DealerPhone]');
    const clickToCallTextFontSize = this.resolveNumericControlValue(this.vdpOffersForm, 'clickToCallTextFontSize', 22);
    const clickToCallBackgroundColor = this.resolveStringControlValue(this.vdpOffersForm, 'clickToCallBackgroundColor', '#000000');
    const clickToCallTextColor = this.resolveStringControlValue(this.vdpOffersForm, 'clickToCallTextColor', '#ffffff');
    const showCtaWithoutOffers = this.resolveBooleanControlValue(this.vdpOffersForm, 'showCtaWithoutOffers', false);
    let privateOfferDisclosure = '';
    if (this.privateOffersForm) {
      const value = this.privateOffersForm.get('privateOfferDisclosure')?.value;
      if (typeof value === 'string') {
        privateOfferDisclosure = value.trim();
      }
    }

    return {
      id: ipopConfiguration.id,
      accountId: this.accountId,
      includePrivateOffersInExternalAdvertising: includePrivateOffersInExternalAdvertising,
      unlockPrivateOffersEnabled: unlockPrivateOffersEnabled,
      vdpOfferWidgetEnabled: vdpOfferWidgetEnabled,
      displaySavingsMessage: displaySavingsMessage,
      savingsMessage: savingsMessage,
      savingsMessageBackgroundColor: savingsMessageBackgroundColor,
      offerTextColor: offerTextColor,
      privateOfferTextColor: privateOfferTextColor,
      privateOfferButtonColor: privateOfferButtonColor,
      showPriceComparison: showPriceComparison,
      displayUnlockedSellingPrice: displayUnlockedSellingPrice,
      preUnlockCtaText: preUnlockCtaText,
      postUnlockCtaText: postUnlockCtaText,
      ctaTextFontSize: ctaTextFontSize,
      ctaButtonColor: ctaButtonColor,
      privateOfferDisclosure: privateOfferDisclosure,
      clickToCallEnabled: clickToCallEnabled,
      clickToCallText: clickToCallText,
      clickToCallTextFontSize: clickToCallTextFontSize,
      clickToCallBackgroundColor: clickToCallBackgroundColor,
      clickToCallTextColor: clickToCallTextColor,
      showCtaWithoutOffers: showCtaWithoutOffers,
    };
  }

  /**
   * Resolve a boolean form control value with a default when the form/control is missing.
   */
  private resolveBooleanControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: boolean): boolean {
    let result = defaultValue;
    if (form) {
      const value = form.get(controlName)?.value;
      if (value === true) {
        result = true;
      }
      if (value === false) {
        result = false;
      }
    }
    return result;
  }

  /**
   * Resolve a trimmed string form control value with a default when missing/blank.
   */
  private resolveStringControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: string): string {
    let result = defaultValue;
    if (form) {
      const value = form.get(controlName)?.value;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== '') {
          result = trimmed;
        }
      }
    }
    return result;
  }

  /**
   * Resolve a number form control value with a default when missing/blank.
   */
  private resolveNumericControlValue(form: UntypedFormGroup | null, controlName: string, defaultValue: number): number {
    let result = defaultValue;
    if (form) {
      const value = form.get(controlName)?.value;
      if (typeof value === 'number') {
       result = value;
      }
    }
    return result;
  }

  /**
   * Handle successful saves.
   */
  private handleSaveSuccess(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Saved',
      detail: 'Settings updated'
    });
    this.markFormsPristine();
  }

  /**
   * Handle failed saves.
   */
  private handleSaveError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save settings'
    });
    this.isSaving = false;
  }

  /**
   * Handle save completion.
   */
  private handleSaveComplete(): void {
    this.isSaving = false;
  }

  /**
   * Mark forms pristine after persisting settings.
   */
  private markFormsPristine(): void {
    if (this.privateOffersForm) {
      this.privateOffersForm.markAsPristine();
    }
    if (this.vdpOffersForm) {
      this.vdpOffersForm.markAsPristine();
    }
  }

  /**
   * Loads account-level registry processing status.
   */
  private loadProcessStatus(): void {
    if (this.isValidAccountId()) {
      this.offerRegistryProcessorSyncService.getProcessStatus(this.accountId)
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (status) => {
            this.handleProcessStatusSuccess(status);
          },
          error: () => {
            this.handleProcessStatusError();
          }
        });
    }
    if (!this.isValidAccountId()) {
      this.handleInvalidAccountId();
    }
  }

  /**
   * Checks whether the selected route account ID can be used for status and queue calls.
   */
  private isValidAccountId(): boolean {
    const isValidAccountId = Number.isInteger(this.accountId) && this.accountId > 0;

    return isValidAccountId;
  }

  /**
   * Handles invalid route account IDs without making backend calls.
   */
  private handleInvalidAccountId(): void {
    this.isProcessStatusInitialized = false;
    this.isProcessStatusLoadFailed = true;
    this.isOfferRegistryProcessingActive = true;
    this.cancelProcessStatusPolling();
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: processStatusLoadErrorDetail
    });
  }

  /**
   * Applies successful process status responses.
   */
  private handleProcessStatusSuccess(status: OfferRegistryProcessStatusInterface): void {
    this.processStatus = status;
    this.isProcessStatusInitialized = true;
    this.isProcessStatusLoadFailed = false;
    this.isOfferRegistryProcessingActive = status.incomplete === true;

    if (this.isOfferRegistryProcessingActive) {
      this.startProcessStatusPolling();
    }
    if (!this.isOfferRegistryProcessingActive) {
      this.cancelProcessStatusPolling();
    }
  }

  /**
   * Handles process status load failures.
   */
  private handleProcessStatusError(): void {
    this.isProcessStatusInitialized = false;
    this.isProcessStatusLoadFailed = true;
    this.isOfferRegistryProcessingActive = true;
    this.cancelProcessStatusPolling();
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: processStatusLoadErrorDetail
    });
  }

  /**
   * Handles successful queue responses.
   */
  private handleProcessRulesSuccess(): void {
    this.markRulesProcessedClean();
    this.showRulesProcessingToast();

    if (!this.isComponentDestroyed) {
      this.loadProcessStatus();
    }
  }

  /**
   * Queues account-level processing with optional component destroy cancellation.
   */
  private queueProcessRulesForAllAccountRules(shouldCancelOnDestroy: boolean): void {
    this.isProcessingRules = true;
    let processRulesRequest = this.offerRegistryProcessorSyncService.queueProcessForAllAccountRules(this.accountId)
      .pipe(
        finalize(() => {
          this.isProcessingRules = false;
        })
      );

    if (shouldCancelOnDestroy) {
      processRulesRequest = processRulesRequest.pipe(takeUntil(this.destroySubject));
    }

    processRulesRequest.subscribe({
      next: () => {
        this.handleProcessRulesSuccess();
      },
      error: (error: unknown) => {
        this.handleProcessRulesError(error, shouldCancelOnDestroy);
      }
    });
  }

  /**
   * Handles queue errors and duplicate in-progress responses.
   */
  private handleProcessRulesError(error: unknown, shouldCancelOnDestroy: boolean): void {
    const isDuplicateQueueResponse = this.isDuplicateQueueResponse(error);

    if (isDuplicateQueueResponse) {
      this.handleDuplicateProcessQueueResponse(shouldCancelOnDestroy);
    }
    if (!isDuplicateQueueResponse) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: processQueueErrorDetail
      });
    }
  }

  /**
   * Refreshes status after a duplicate queue response.
   */
  private handleDuplicateProcessQueueResponse(shouldCancelOnDestroy: boolean): void {
    let processStatusRequest = this.offerRegistryProcessorSyncService.getProcessStatus(this.accountId);

    if (shouldCancelOnDestroy) {
      processStatusRequest = processStatusRequest.pipe(takeUntil(this.destroySubject));
    }

    processStatusRequest.subscribe({
      next: (status) => {
        if (!this.isComponentDestroyed) {
          this.handleDuplicateProcessStatusSuccess(status);
        }
      },
      error: () => {
        if (!this.isComponentDestroyed) {
          this.handleProcessStatusError();
        }
      }
    });
  }

  /**
   * Treats duplicate queue response as in-progress only when status confirms it.
   */
  private handleDuplicateProcessStatusSuccess(status: OfferRegistryProcessStatusInterface): void {
    this.handleProcessStatusSuccess(status);

    if (status.incomplete === true && status.accountId === this.accountId) {
      this.markRulesProcessedClean();
      this.showRulesProcessingToast();
    }
    if (status.incomplete !== true || status.accountId !== this.accountId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: processQueueErrorDetail
      });
    }
  }

  /**
   * Checks whether a queue error is a duplicate command response.
   */
  private isDuplicateQueueResponse(error: unknown): boolean {
    let isDuplicateQueueResponse = false;

    if (error instanceof HttpErrorResponse && error.status === duplicateQueueStatusCode) {
      isDuplicateQueueResponse = true;
    }

    return isDuplicateQueueResponse;
  }

  /**
   * Marks rule configuration as processed/clean.
   */
  private markRulesProcessedClean(): void {
    this.isRuleConfigurationDirty = false;
  }

  /**
   * Shows required queue success toast.
   */
  private showRulesProcessingToast(): void {
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: rulesProcessingToastDetail
    });
  }

  /**
   * Starts account-level process status polling while processing is active.
   */
  private startProcessStatusPolling(): void {
    this.cancelProcessStatusPolling();
    this.offerRegistryProcessorSyncService.getProcessStatus(this.accountId)
      .pipe(
        repeat({delay: processStatusPollingIntervalMs}),
        takeUntil(this.processStatusPollingSubject),
        takeUntil(this.destroySubject)
      )
      .subscribe({
        next: (status) => {
          this.handleProcessPollingStatus(status);
        },
        error: () => {
          this.handleProcessStatusError();
        }
      });
  }

  /**
   * Applies process polling responses.
   */
  private handleProcessPollingStatus(status: OfferRegistryProcessStatusInterface): void {
    this.processStatus = status;
    this.isProcessStatusInitialized = true;
    this.isProcessStatusLoadFailed = false;
    this.isOfferRegistryProcessingActive = status.incomplete === true;

    if (!this.isOfferRegistryProcessingActive) {
      this.cancelProcessStatusPolling();
    }
  }

  /**
   * Stops account-level process status polling.
   */
  private cancelProcessStatusPolling(): void {
    this.processStatusPollingSubject.next();
  }

  /**
   * Checks whether dirty rule navigation should attempt queueing.
   */
  private shouldQueueDirtyRuleProcessingForNavigation(): boolean {
    return this.hasDirtyRuleConfiguration()
      && !this.isProcessingRules
      && !this.isOfferRegistryProcessingActive
      && this.isValidAccountId();
  }

  /**
   * Show native prompt on browser/tab refresh or close
   */
  @HostListener('window:beforeunload', ['$event'])
  confirmOnBrowserClose(event: BeforeUnloadEvent): void {
    let shouldPrompt = false;
    if (this.privateOffersForm) {
      if (this.privateOffersForm.dirty) {
        shouldPrompt = true;
      }
    }
    if (this.vdpOffersForm) {
      if (this.vdpOffersForm.dirty) {
        shouldPrompt = true;
      }
    }
    if (this.hasDirtyRuleConfiguration()) {
      shouldPrompt = true;
      this.queueDirtyRuleProcessingForNavigationAttempt();
    }
    if (shouldPrompt) {
      event.returnValue = dirtyRuleNavigationConfirmationMessage;
    }
  }
}
